import { useEffect, useRef, useState, type ReactNode } from "react";
import { ChevronDown, ChevronUp, ListOrdered, Pencil, Send, Trash2, Undo2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import type { HermesQueuedMessage } from "@/lib/hermes-send-queue";
import { parsePickEditChatDisplay } from "@/lib/brand-site-preview-nav";
import { cn } from "@/lib/utils";
import { PickEditCommandCard } from "./PickEditCommandCard";

type Props = {
  items: HermesQueuedMessage[];
  disabled?: boolean;
  onUpdate: (id: string, content: string) => void;
  onRemove: (id: string) => void;
  /** 拉回输入框 */
  onPull: (id: string) => void;
  /** 打断当前生成并立刻发送 */
  onSendNow: (id: string) => void;
};

/** 生成中的待发队列：编辑 / 拉出 / 删除 / 插队发送。 */
export function HermesSendQueueList({
  items,
  disabled = false,
  onUpdate,
  onRemove,
  onPull,
  onSendNow,
}: Props) {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState("");
  const [expandedIds, setExpandedIds] = useState<Record<string, boolean>>({});

  if (items.length === 0) return null;

  function startEdit(item: HermesQueuedMessage) {
    setEditingId(item.id);
    setDraft(item.content);
  }

  function commitEdit(id: string) {
    onUpdate(id, draft);
    setEditingId(null);
    setDraft("");
  }

  function toggleExpanded(id: string) {
    setExpandedIds((current) => ({ ...current, [id]: !current[id] }));
  }

  return (
    <div className="mb-2 rounded-lg border border-border bg-muted/30 px-2 py-1.5">
      <div className="mb-1 flex items-center gap-1.5 text-[11px] text-muted-foreground">
        <ListOrdered className="size-3.5" />
        <span>发送队列 · {items.length}</span>
        <span className="text-muted-foreground/80">完成后自动发送；Stop 后需手动发或插队</span>
      </div>
      <ul className="flex flex-col gap-1.5">
        {items.map((item, index) => {
          const editing = editingId === item.id;
          const expanded = Boolean(expandedIds[item.id]);
          return (
            <li
              key={item.id}
              className={cn(
                "rounded-md border border-border/80 bg-background px-2 py-1.5",
                index === 0 && "border-primary/40",
              )}
            >
              {editing ? (
                <div className="flex flex-col gap-1.5">
                  <textarea
                    value={draft}
                    onChange={(event) => setDraft(event.target.value)}
                    rows={2}
                    autoFocus
                    className="w-full resize-none rounded-md border border-border bg-background px-2 py-1 text-xs outline-none focus:border-primary"
                    onKeyDown={(event) => {
                      if (event.key === "Enter" && !event.shiftKey) {
                        event.preventDefault();
                        commitEdit(item.id);
                      }
                      if (event.key === "Escape") {
                        setEditingId(null);
                        setDraft("");
                      }
                    }}
                  />
                  <div className="flex justify-end gap-1">
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="h-7 px-2 text-xs"
                      onClick={() => {
                        setEditingId(null);
                        setDraft("");
                      }}
                    >
                      取消
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      className="h-7 px-2 text-xs"
                      disabled={!draft.trim()}
                      onClick={() => commitEdit(item.id)}
                    >
                      保存
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start gap-1.5">
                  <QueuedBody
                    content={item.content}
                    expanded={expanded}
                    onToggle={() => toggleExpanded(item.id)}
                  />
                  <div className="flex shrink-0 items-center gap-0.5">
                    <IconAction
                      label="编辑"
                      disabled={disabled}
                      onClick={() => startEdit(item)}
                    >
                      <Pencil className="size-3.5" />
                    </IconAction>
                    <IconAction
                      label="拉回输入框"
                      disabled={disabled}
                      onClick={() => onPull(item.id)}
                    >
                      <Undo2 className="size-3.5" />
                    </IconAction>
                    <IconAction
                      label="立刻发送（打断当前）"
                      disabled={disabled}
                      onClick={() => onSendNow(item.id)}
                    >
                      <Send className="size-3.5" />
                    </IconAction>
                    <IconAction
                      label="删除"
                      disabled={disabled}
                      onClick={() => onRemove(item.id)}
                    >
                      <Trash2 className="size-3.5" />
                    </IconAction>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function QueuedBody({
  content,
  expanded,
  onToggle,
}: {
  content: string;
  expanded: boolean;
  onToggle: () => void;
}) {
  const textRef = useRef<HTMLParagraphElement>(null);
  const [overflows, setOverflows] = useState(false);
  const pickEdit = parsePickEditChatDisplay(content);
  const isPickEdit = pickEdit != null;

  useEffect(() => {
    if (isPickEdit) return;
    const el = textRef.current;
    if (!el) return;
    const measure = () => {
      // 折叠态用 line-clamp 测是否裁切；展开态对照两行高度
      if (expanded) {
        const lineHeight = Number.parseFloat(getComputedStyle(el).lineHeight) || 16;
        setOverflows(el.scrollHeight > lineHeight * 2 + 2);
        return;
      }
      setOverflows(el.scrollHeight > el.clientHeight + 1);
    };
    measure();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, [content, expanded, isPickEdit]);

  if (pickEdit) {
    return (
      <div className="min-w-0 flex-1">
        <PickEditCommandCard
          parsed={pickEdit}
          raw={content}
          align="start"
          compact
          showRawToggle={false}
        />
        {expanded ? (
          <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap break-words rounded-md bg-muted/40 p-1.5 text-[10px] leading-snug text-muted-foreground">
            {content}
          </pre>
        ) : null}
        <button
          type="button"
          onClick={onToggle}
          className="mt-0.5 inline-flex items-center gap-0.5 text-[11px] text-muted-foreground hover:text-foreground"
        >
          {expanded ? (
            <>
              收起 <ChevronUp className="size-3" />
            </>
          ) : (
            <>
              详情 <ChevronDown className="size-3" />
            </>
          )}
        </button>
      </div>
    );
  }

  return (
    <div className="min-w-0 flex-1">
      <p
        ref={textRef}
        className={cn(
          "whitespace-pre-wrap break-words text-xs leading-snug text-foreground",
          !expanded && "line-clamp-2",
        )}
      >
        {content}
      </p>
      {overflows ? (
        <button
          type="button"
          onClick={onToggle}
          className="mt-0.5 inline-flex items-center gap-0.5 text-[11px] text-muted-foreground hover:text-foreground"
        >
          {expanded ? (
            <>
              收起 <ChevronUp className="size-3" />
            </>
          ) : (
            <>
              展开 <ChevronDown className="size-3" />
            </>
          )}
        </button>
      ) : null}
    </div>
  );
}

function IconAction({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string;
  disabled?: boolean;
  onClick: () => void;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      title={label}
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground disabled:pointer-events-none disabled:opacity-40"
    >
      {children}
    </button>
  );
}
